import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Icon, Button } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { getEventName } from '@op/pages/Overview/KeyMetrics/utils';
import { arrayToSimpleObj, sortTimeListFunc } from '@op/utils';
import CardCommon from '@op/components/CardCommon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { formatTimeToShow } from '@op/utils/time';
import { useSyncState } from '@op/hooks/common';
import { useUpdateEffect } from 'ahooks';
import { normalizeOpinionDateType } from '../hooks/utils/getTimelineTimeFormat';
import { resolveOpinionChartXAxisValue } from '../hooks/utils/resolveOpinionChartXAxisValue';
import {
  buildEventSettingSelectValue,
  getOpinionEventOnlyOneKey,
} from '../hooks/utils/buildEventSettingSelectValue';
import { getOpinionEventCategoryMeta } from '../hooks/utils/getOpinionEventCategoryLabel';
import type { IOpinionChartEventSetting } from '../hooks/types';
import { OPINION_BAR_AND_LINE_CHART_ROOT_CLASS } from '../constants';
import '@op/pages/Overview/KeyMetrics/MentionsSentiment/components/EventSetting/index.scss';

interface IOpinionEventSettingProps {
  visible: boolean;
  close: () => void;
  confirm: (setting: IOpinionChartEventSetting) => void;
  data: Record<string, any[]>;
  timeFormat?: string;
  showEventAxisValue?: boolean;
  showEventBubblesValue?: boolean;
  dateType?: string;
  xAxisData?: string[];
  /** 图表当前展示的事件，用于回填勾选 */
  bubbleEvent?: Record<string, any[]>;
}

/**
 * 情报侧事件设置弹窗：按 bubbleEvent 回填勾选（含 In-game Events），不改舆情组件。
 */
export default function OpinionEventSetting({
  visible,
  close,
  confirm,
  data,
  timeFormat = 'YYYY-MM-DD',
  showEventAxisValue,
  showEventBubblesValue,
  dateType,
  xAxisData = [],
  bubbleEvent = {},
}: IOpinionEventSettingProps) {
  const { formatMessage } = useIntl();
  const lang = getLocale();
  const normalizedDateType = normalizeOpinionDateType(dateType);

  const {
    state: selectValue,
    setState: setSelectValue,
    syncStates: syncSelectValue,
    revertToPrevious: revertSelectValue,
    initStates: initSelectValue,
  } = useSyncState<Record<string, string[]>>({});

  const {
    state: showEventAxis,
    setState: setShowEventAxis,
    syncStates: syncShowEventAxis,
    revertToPrevious: revertShowEventAxis,
    initStates: initShowEventAxis,
  } = useSyncState<boolean>(!!showEventAxisValue);

  const {
    state: showEventBubbles,
    setState: setShowEventBubbles,
    syncStates: syncShowEventBubbles,
    revertToPrevious: revertShowEventBubbles,
    initStates: initShowEventBubbles,
  } = useSyncState<boolean>(!!showEventBubblesValue);

  useUpdateEffect(() => {
    initShowEventAxis(!!showEventAxisValue);
  }, [showEventAxisValue]);

  useUpdateEffect(() => {
    initShowEventBubbles(!!showEventBubblesValue);
  }, [showEventBubblesValue]);

  function closeWindow() {
    close();
    revertSelectValue();
    revertShowEventBubbles();
    revertShowEventAxis();
  }

  function syncAllStates() {
    syncSelectValue();
    syncShowEventAxis();
    syncShowEventBubbles();
  }

  const changeSelectValue = (value: any, key?: string) => {
    if (key) {
      setSelectValue(prev => ({ ...prev, [key]: value }));
    } else {
      setSelectValue(value);
    }
  };

  const [levelOneActive, setLevelOneActive] = useState('');
  const [levelOneOptions, setLevelOneOptions] = useState<
    Array<{
      name: string;
      name_cn: string;
      value: string;
      children: string[];
    }>
  >([]);

  function getCallbackEventList(nextSelectValue: Record<string, string[]>) {
    const selectedList = Object.values(nextSelectValue || {}).flat(2);
    const list = Object.values(data || {})
      .flat(3)
      .filter((item: any) =>
        selectedList.includes(getOpinionEventOnlyOneKey(item)),
      );

    list.sort(sortTimeListFunc('start_time', 'desc'));
    const result: Record<string, any[]> = {};

    list.forEach((item: any) => {
      const { realValue: time } = resolveOpinionChartXAxisValue(
        item.start_time,
        normalizedDateType,
        timeFormat,
        xAxisData,
      );
      if (!result[time]) {
        result[time] = [];
      }
      result[time].push(item);
    });

    return result;
  }

  useEffect(() => {
    const nextLevelOneOptions = Object.keys(data || {}).map(key => {
      const list = data[key] || [];
      const children = list.map((eventItem: any) =>
        getOpinionEventOnlyOneKey(eventItem),
      );

      return {
        ...getOpinionEventCategoryMeta(key, list),
        value: key,
        children,
      };
    });

    const selected = buildEventSettingSelectValue(data, bubbleEvent);

    initSelectValue(selected);
    setLevelOneActive(nextLevelOneOptions?.[0]?.value || '');
    setLevelOneOptions(nextLevelOneOptions);
    // 仅跟随 data 初始化；弹窗每次打开都会重新挂载，此时 bubbleEvent 已是最新
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { levelOneSelect, levelOneIndeterminate, levelOneIsClear } =
    useMemo(() => {
      const result: any = {
        levelOneSelect: {},
        levelOneIndeterminate: {},
        levelOneIsClear: false,
      };

      Object.keys(data || {}).forEach(key => {
        const element = data[key] || [];
        const selected = selectValue[key] || [];

        if (element.length === selected.length && element.length > 0) {
          result.levelOneSelect[key] = true;
          result.levelOneIsClear = true;
        } else if (selected.length > 0) {
          result.levelOneIndeterminate[key] = true;
          result.levelOneIsClear = true;
        } else {
          result.levelOneSelect[key] = false;
        }
      });

      return result;
    }, [selectValue, data]);

  const handleLevelOneToggleClearBtn = () => {
    if (levelOneIsClear) {
      return changeSelectValue(
        Object.keys(selectValue).reduce(
          (pre, key) => ({ ...pre, [key]: [] }),
          {},
        ),
      );
    }

    const item = arrayToSimpleObj(levelOneOptions, 'children', 'value');
    changeSelectValue(item);
  };

  const levelTwoOptions = useMemo(() => {
    const list = (data?.[levelOneActive] || []).map((item: any) => ({
      ...item,
      value: getOpinionEventOnlyOneKey(item),
    }));
    list.sort(sortTimeListFunc('start_time', 'desc'));
    return list;
  }, [levelOneActive, data]);

  const { levelTwoSelect, levelTwoIsClear } = useMemo(() => {
    const value = selectValue[levelOneActive] || [];
    return {
      levelTwoSelect: value,
      levelTwoIsClear: value.length > 0,
    };
  }, [selectValue, levelOneActive]);

  const handleLevelTwoToggleClearBtn = () => {
    if (levelTwoIsClear) {
      return changeSelectValue([], levelOneActive);
    }

    const item = levelOneOptions.find(
      (option: any) => option.value === levelOneActive,
    );
    return changeSelectValue(item?.children || [], levelOneActive);
  };

  return (
    <Modal
      className={`${OPINION_BAR_AND_LINE_CHART_ROOT_CLASS} dbo-modal-event-setting`}
      visible={visible}
      caption={formatMessage({ id: 'Events Setting' })}
      onClose={() => closeWindow()}
    >
      <Modal.Body>
        <div className="dbo-event-setting dbc-region-select-container">
          <div className="dbo-event-setting-hd">
            <div className="dbo-event-setting-tips">
              {formatMessage({
                id: 'When too many events are selected, some may not be fully displayed on the chart due to space constraints. To view all the events, hover your mouse over the numbers below the axis.',
              })}
            </div>
            <Checkbox
              value={showEventAxis}
              onChange={value => {
                setShowEventAxis(value);
                buriedBtnPoint('KMLG_EventSetting_axis', value);
              }}
            >
              {formatMessage({ id: 'Show event axis' })}
            </Checkbox>
            <Checkbox
              value={showEventBubbles}
              onChange={value => {
                setShowEventBubbles(value);
                buriedBtnPoint('KMLG_EventSetting_Bubble', value);
              }}
            >
              {formatMessage({ id: 'Show event bubbles' })}
            </Checkbox>
          </div>
          <div className="dbo-event-setting-bd">
            <CardCommon
              contentCard
              empty={levelOneOptions.length === 0}
              emptyHeight={280}
              hasEmptyTitle={false}
              emptyDescText="There are no events scheduled for this period."
            >
              <div className="dbc-region-select-content-container">
                <div className="dbc-region-select-content-wrap">
                  <div className="dbc-region-select-content-title-wrap">
                    <span>{formatMessage({ id: 'Set display events' })}</span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelOneToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelOneIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    {levelOneOptions.map(item => (
                      <div
                        key={item.value}
                        onClick={() => setLevelOneActive(item.value)}
                        className={`dbc-region-select-checkbox ${
                          levelOneActive === item.value ? 'active' : ''
                        }`}
                      >
                        <Checkbox
                          value={levelOneSelect[item.value]}
                          indeterminate={levelOneIndeterminate[item.value]}
                          onChange={value => {
                            setLevelOneActive(item.value);
                            const selected = value ? item.children : [];
                            changeSelectValue(selected, item.value);
                          }}
                        />
                        <div className="dbc-region-select-checkbox-name-wrap">
                          <span className="dbc-region-select-checkbox-name">
                            {getEventName(item, lang)} ({item.children.length})
                          </span>
                          <Icon type="arrowright" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="dbc-region-select-content-wrap dbc-region-select-content-wrap--right">
                  <div className="dbc-region-select-content-title-wrap">
                    <span className="dbc-region-select-content-country-title">
                      {formatMessage({ id: 'Event' })}
                    </span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelTwoToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelTwoIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    <Checkbox.Group
                      key={levelOneActive}
                      value={levelTwoSelect}
                      onChange={value => {
                        changeSelectValue(value, levelOneActive);
                      }}
                    >
                      {levelTwoOptions.map((item: any) => (
                        <Checkbox
                          name={item.value}
                          key={item.value}
                          className="dbc-region-select-checkbox"
                        >
                          <div className="dbc-region-select-checkbox-name-wrap">
                            <span className="dbc-region-select-checkbox-name">
                              {getEventName(item, lang)}
                            </span>
                            <span className="dbc-region-select-checkbox-date">
                              {formatTimeToShow({
                                ...item,
                                date_type: 'hourly',
                                hourlyIsSame: true,
                              })}
                            </span>
                          </div>
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </div>
                </div>
              </div>
            </CardCommon>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            syncAllStates();
            confirm({
              bubbleEvent: getCallbackEventList(selectValue),
              showEventAxis,
              showEventBubbles,
            });
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={() => closeWindow()}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
